import { Card, EmptyView, ErrorView, LoadingView, StatusBadge, DashStyles as Styles, TableWrap } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Button, Label } from '@app/engine';
import { CommonConstants, FormatTimestamp } from '@shared/common';
import { IntegrationClient, Telemetry } from '@shared/frontend';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { IIntegrationDTO, ISchedulingResource } from '@shared/common';

/** File-local constants for the Integrations screen. */
const localConstants = {
  schedulingKind: 'scheduling',
  // Query keys appended by the OAuth callback redirect.
  queryConnected: 'connected',
  queryIntegration: 'integration',
  queryError: 'integrationError'
} as const;

/** A scheduling provider the tenant can connect, in display order, mapped to its label localization key (labels are resolved per render so they reflow on a language change). */
const providerLabelKeys: { slug: string; key: string }[] = [
  { slug: CommonConstants.Database.SchedulingProvider.GoogleCalendar, key: 'IntegrationsProviderGoogleCalendar' },
  { slug: CommonConstants.Database.SchedulingProvider.MicrosoftOutlook, key: 'IntegrationsProviderMicrosoftOutlook' }
];

/**
 * Resolves a provider slug to its (currently active language) display label, falling back to the raw slug.
 *
 * @param slug - The provider slug.
 * @returns The display label.
 */
function providerLabel(slug: string): string {
  const entry = providerLabelKeys.find((provider) => provider.slug === slug);
  return entry ? Localized[entry.key] : slug;
}

/**
 * Integrations screen: connect scheduling providers via OAuth, choose the calendar / service the assistant uses, and disconnect.
 *
 * @returns The integrations element.
 */
export function Integrations() {
  const { tenantID } = useDashboard();
  const { langVersion } = useLocale();
  const { data, loading, error, reload } = useDashboardData((signal) => IntegrationClient.ListIntegrations(signal, tenantID!), [tenantID]);

  // The connectable providers with their display labels resolved for the active language, recomputed on a language change (the slug list itself is constant).
  const connectableProviders = useMemo(() => {
    void langVersion;
    return providerLabelKeys.map((provider) => ({ slug: provider.slug, label: Localized[provider.key] }));
  }, [langVersion]);

  // The single connected calendar new bookings are written to (null when none is connected).
  const activeSchedulingIntegrationID = data?.activeSchedulingIntegrationID ?? null;

  // Connected scheduling integrations (OAuth completed) shown in the read-only grid; provider connect/pick/disconnect lives in the Connect panel. Deduped to one row per provider — the active
  // calendar wins, otherwise the most recently connected — so churned connect/disconnect cycles never stack duplicate rows for the same provider.
  const connectedRows = Object.values(
    (data?.integrations ?? [])
      .filter((integration) => integration.kind === localConstants.schedulingKind && Boolean(integration.connectedAt))
      .reduce<Record<string, IIntegrationDTO>>((byProvider, integration) => {
        const existing = byProvider[integration.provider];
        const wins =
          !existing ||
          integration.id === activeSchedulingIntegrationID ||
          (existing.id !== activeSchedulingIntegrationID && new Date(integration.connectedAt!).getTime() > new Date(existing.connectedAt!).getTime());
        if (wins) byProvider[integration.provider] = integration;
        return byProvider;
      }, {})
  );

  // Per-integration loaded resource lists for the picker.
  const [resources, setResources] = useState<Record<string, ISchedulingResource[]>>({});
  // Per-integration chosen target ids.
  const [selected, setSelected] = useState<Record<string, string>>({});
  // Slug or integration id currently performing an async action (drives button busy states).
  const [busy, setBusy] = useState<string | null>(null);
  // Integration id awaiting confirmation before becoming the active booking calendar.
  const [pendingActive, setPendingActive] = useState<string | null>(null);
  // Transient header feedback, split per panel: list-card actions (activate / switch / disconnect) and connect-card events (OAuth start, callback result).
  const [listFeedback, setListFeedback] = useState<IFeedbackDTO | null>(null);
  const [connectFeedback, setConnectFeedback] = useState<IFeedbackDTO | null>(null);

  /** Loads the selectable resources for an integration into local state. */
  const loadResources = useCallback(
    async (integrationID: string) => {
      try {
        const list = await IntegrationClient.ListResources(new AbortController().signal, tenantID!, integrationID);
        setResources((prev) => ({ ...prev, [integrationID]: list }));
      } catch {
        setConnectFeedback({ text: Localized.IntegrationsConnectError, type: 'error' });
      }
    },
    [tenantID]
  );

  // On return from an OAuth callback, surface the result and auto-open the resource picker, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get(localConstants.queryConnected);
    const integrationID = params.get(localConstants.queryIntegration);
    const callbackError = params.get(localConstants.queryError);
    if (callbackError) setConnectFeedback({ text: Localized.IntegrationsConnectError, type: 'error' });
    if (connected && integrationID) {
      setConnectFeedback({ text: Localized.IntegrationsConnectedNotice, type: 'success' });
      void loadResources(integrationID);
    }
    if (connected || integrationID || callbackError) {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [loadResources]);

  /**
   * Starts connecting a provider via the OAuth redirect handshake.
   *
   * @param slug - The provider slug to connect.
   */
  async function handleConnect(slug: string) {
    setConnectFeedback(null);
    setBusy(slug);
    try {
      const result = await IntegrationClient.ConnectScheduling(new AbortController().signal, tenantID!, slug);
      Telemetry.IntegrationConnectStarted(slug);
      window.location.assign(result.authorizeURL);
    } catch {
      setConnectFeedback({ text: Localized.IntegrationsConnectError, type: 'error' });
    } finally {
      setBusy(null);
    }
  }

  /**
   * Persists the chosen resource and activates the integration.
   *
   * @param integrationID - The integration to activate.
   */
  async function handleActivate(integrationID: string) {
    const targetID = selected[integrationID];
    if (!targetID) return;
    setBusy(integrationID);
    setListFeedback(null);
    try {
      await IntegrationClient.SelectResource(new AbortController().signal, tenantID!, integrationID, targetID);
      Telemetry.IntegrationActivated(data?.integrations.find((entry) => entry.id === integrationID)?.provider);
      reload();
    } catch {
      setListFeedback({ text: Localized.IntegrationsConnectError, type: 'error' });
    } finally {
      setBusy(null);
    }
  }

  /**
   * Disconnects an integration.
   *
   * @param integrationID - The integration to disconnect.
   */
  async function handleDisconnect(integrationID: string) {
    setBusy(integrationID);
    setListFeedback(null);
    try {
      await IntegrationClient.Disconnect(new AbortController().signal, tenantID!, integrationID);
      Telemetry.IntegrationDisconnected(data?.integrations.find((entry) => entry.id === integrationID)?.provider);
      reload();
    } catch {
      setListFeedback({ text: Localized.IntegrationsConnectError, type: 'error' });
    } finally {
      setBusy(null);
    }
  }

  /**
   * Makes a connected scheduling integration the tenant's single active booking calendar. The previously active calendar stays connected; only the pointer moves.
   *
   * @param integrationID - The integration to make active.
   */
  async function handleSetActive(integrationID: string) {
    setBusy(integrationID);
    setListFeedback(null);
    try {
      await IntegrationClient.SetActiveScheduling(new AbortController().signal, tenantID!, integrationID);
      Telemetry.IntegrationActivated(data?.integrations.find((entry) => entry.id === integrationID)?.provider);
      setPendingActive(null);
      reload();
    } catch {
      setListFeedback({ text: Localized.IntegrationsConnectError, type: 'error' });
    } finally {
      setBusy(null);
    }
  }

  /**
   * Renders the per-row actions: a resource picker for connected-but-inactive scheduling integrations, or, for connected ones, the active-calendar badge or a switch control plus disconnect.
   *
   * @param integration - The integration row.
   * @returns The actions element, or null for non-scheduling integrations.
   */
  function renderActions(integration: IIntegrationDTO) {
    if (integration.kind !== localConstants.schedulingKind) return null;
    const isBusy = busy === integration.id;
    if (integration.status === CommonConstants.Database.Status.Active) {
      const isActiveCalendar = integration.id === activeSchedulingIntegrationID;
      const isConfirming = pendingActive === integration.id;
      return (
        <div className={Styles.actionsRow}>
          {isActiveCalendar ? (
            <Label classNameContainer={`${Styles.badge} ${Styles.badgeSuccess}`} text={Localized.IntegrationsActiveBadge} />
          ) : isConfirming ? (
            <div className={Styles.confirmBubble}>
              <Label text={Localized.IntegrationsConfirmActive} />
              <div className={Styles.headerActions}>
                <Button
                  text={isBusy ? Localized.IntegrationsMakingActive : Localized.IntegrationsConfirmActiveAction}
                  disabled={isBusy}
                  classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                  classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
                  onClickHandler={() => void handleSetActive(integration.id)}
                />
                <Button
                  text={Localized.SharedCancel}
                  disabled={isBusy}
                  classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
                  classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
                  onClickHandler={() => setPendingActive(null)}
                />
              </div>
            </div>
          ) : (
            <Button
              text={Localized.IntegrationsMakeActive}
              disabled={isBusy}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
              onClickHandler={() => {
                setListFeedback(null);
                setPendingActive(integration.id);
              }}
            />
          )}
          <Button
            text={isBusy ? Localized.IntegrationsDisconnecting : Localized.IntegrationsDisconnect}
            disabled={isBusy}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnDanger} ${Styles.btnSmall}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnDanger} ${Styles.btnSmall}`}
            onClickHandler={() => void handleDisconnect(integration.id)}
          />
        </div>
      );
    }
    const list = resources[integration.id];
    // Credential-less pending row (OAuth abandoned mid-consent, or previously disconnected): the connect never completed, so listing resources would only fail. Offer a reconnect, which reuses
    // this same integration row server-side (no dead rows accumulate). `connectedAt` is set by the OAuth callback exactly when credentials are stored, and cleared on disconnect.
    if (!integration.connectedAt) {
      const isReconnecting = busy === integration.provider;
      // The incomplete state itself is shown as a badge in the Connected column; Actions carries only the remedy.
      return (
        <div className={Styles.actionsRow}>
          <Button
            text={isReconnecting ? Localized.IntegrationsConnecting : Localized.IntegrationsReconnect}
            disabled={isReconnecting}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
            onClickHandler={() => void handleConnect(integration.provider)}
          />
        </div>
      );
    }
    return (
      <div className={Styles.actionsRow}>
        {list ? (
          <select className={Styles.select} value={selected[integration.id] ?? ''} onChange={(event) => setSelected((prev) => ({ ...prev, [integration.id]: event.target.value }))}>
            <option value="">{Localized.IntegrationsResourcePlaceholder}</option>
            {list.map((resource) => (
              <option key={resource.id} value={resource.id}>
                {resource.name}
              </option>
            ))}
          </select>
        ) : (
          <Button
            text={Localized.IntegrationsChooseResource}
            disabled={isBusy}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
            onClickHandler={() => void loadResources(integration.id)}
          />
        )}
        <Button
          text={isBusy ? Localized.IntegrationsActivating : Localized.IntegrationsActivate}
          disabled={isBusy || !(selected[integration.id] ?? '')}
          classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
          classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
          onClickHandler={() => void handleActivate(integration.id)}
        />
      </div>
    );
  }

  return (
    // 2:1 split row: the provider table gets two thirds, the narrower Connect panel one third; tight containers (portrait) wrap them into full-width stacked rows. Heights flow independently.
    <div className={`${Styles.cardRow} ${Styles.cardRowSplit} ${Styles.cardRowTop}`}>
      <Card title={Localized.IntegrationsTitle} titleHint={Localized.IntegrationsListHint} feedback={<FeedbackMessage feedback={listFeedback} />}>
        {loading ? (
          <LoadingView className={Styles.panelLoading} />
        ) : error ? (
          <ErrorView onRetry={reload} />
        ) : connectedRows.length === 0 ? (
          <EmptyView message={Localized.IntegrationsNone} />
        ) : (
          <TableWrap>
            <table className={Styles.table}>
              <thead>
                <tr>
                  <th>
                    <Label text={Localized.IntegrationsProvider} />
                  </th>
                  <th>
                    <Label text={Localized.IntegrationsCalendar} />
                  </th>
                  <th>
                    <Label text={Localized.SharedStatus} />
                  </th>
                  <th>
                    <Label text={Localized.SharedConnected} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {connectedRows.map((integration) => (
                  <tr key={integration.id}>
                    <td>
                      <Label text={providerLabel(integration.provider)} />
                    </td>
                    <td>
                      <Label text={integration.externalAccountID ?? Localized.IntegrationsCalendarDefault} />
                    </td>
                    <td>
                      {integration.id === activeSchedulingIntegrationID ? (
                        <Label classNameContainer={`${Styles.badge} ${Styles.badgeSuccess}`} text={Localized.IntegrationsActiveBadge} />
                      ) : (
                        <StatusBadge status={integration.status} />
                      )}
                    </td>
                    <td>
                      <Label text={FormatTimestamp(integration.connectedAt!)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card title={Localized.IntegrationsConnectTitle} titleHint={Localized.IntegrationsConnectHint} feedback={<FeedbackMessage feedback={connectFeedback} />}>
        <Label classNameContainer={Styles.muted} text={Localized.IntegrationsConnectIntro} />
        <div className={Styles.cardStack}>
          {connectableProviders.map((provider) => {
            const integration = (data?.integrations ?? []).find((entry) => entry.provider === provider.slug && entry.kind === localConstants.schedulingKind);
            const isBusy = busy === provider.slug;
            return (
              <div key={provider.slug} className={Styles.subPanel}>
                <Label classNameContainer={Styles.textStrong} text={provider.label} />
                {integration ? (
                  renderActions(integration)
                ) : (
                  <Button
                    text={isBusy ? Localized.IntegrationsConnecting : Localized.IntegrationsConnectAction}
                    disabled={isBusy}
                    classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`}
                    classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary}`}
                    onClickHandler={() => void handleConnect(provider.slug)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
